import { useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { LicenseAction } from '../../../actions/LicenseAction';
import { isDealerRole } from '../../../lib/licenseHelper';
import { Translate as t } from '../../../config/Translate';
import { LicenseIds } from '../../../actions/constants/LicenseConstant';
import { LICENSE_PRODUCTS } from '../../../actions/constants/LicenseProductConstant';
import LicenseProductStatusCard, { LicenseStatusCell, LicenseStatusCounts } from './LicenseProductStatusCard';

// 바우처 제품(LICENSE_PRODUCTS)은 LicenseProductStatusCard 가 그린다 — 여기는 그 밖의 flat 제품(5D·RAYFace)만.
// xl(1200px) 이상은 카드 한 줄에 하나(왼쪽 열), 그 아래는 가로로 줄바꿈(576px 미만은 한 줄에 하나).
const CARD_COL_CLASS = 'col-12 col-sm-6 col-lg-3 col-xl-12';

const FLAT_LICENSE_IDS = LicenseIds.filter((def) => !LICENSE_PRODUCTS.some((p) => p.licenseAppId === def.id));

// 왼쪽 카드 열 — 바우처 제품 카드는 눌러 허브의 제품을 고르고, 5D·RAYFace 는 현황만 보인다.
const LicenseOverviewTab = ({ user, role, licenses, selectedProductKey, onSelectProduct }) => {
    const dispatch = useDispatch();
    const isDealer = isDealerRole(role);

    useEffect(() => {
        if (!user?.companyId) return;
        // 딜러는 5D·RAYFace 칸을 그리지 않는다 — 상위 법인 값 노출 방지라 조회도 하지 않는다.
        if (isDealer) return;
        // BUG-177: 각 조직은 자기 라이선스만 표시한다. 이전에는 자기 조회가 비면 parentCompanyId 로
        // 폴백해 부모(상위 법인) 라이선스를 보여줬으나, 중간 법인/딜러가 상위 것을 보는 것은
        // 의미가 없어 제거(사용자 결정). 자기 것이 없으면 빈 목록으로 표시.
        dispatch(LicenseAction.GetLicenseByCompanyId(user.companyId));
    }, [dispatch, user?.companyId, isDealer]);

    const renderFlatLicense = (def) => {
        const found = (licenses || []).find((l) => l._id === def.id);
        // t_initial/t_renewal 구조이면 split 타입, t_package 구조이면 single 타입으로 분류.
        // flat 제품은 Σ하위가 없는 t−u = Self-Usable(용어집 §3).
        const isSplitType = found && (found.t_initial !== undefined || found.t_renewal !== undefined);
        const item = isSplitType
            ? {
                type: 'split',
                initialRemaining: (found.t_initial ?? 0) - (found.u_initial ?? 0),
                renewalRemaining: (found.t_renewal ?? 0) - (found.u_renewal ?? 0),
                initialLabelKey: 'license.label.initialSelfUsable',
                renewalLabelKey: 'license.label.renewalSelfUsable',
            }
            : {
                type: 'single',
                t_package: found?.t_package ?? 0,
                u_package: found?.u_package ?? 0,
                // FR-009: 단일 카드(t_package − u_package)는 Σ하위가 없는 t−u = Self-Usable 로 수렴.
                singleLabelKey: 'license.label.selfUsable',
            };
        return (
            <div key={def.id} className={CARD_COL_CLASS}>
                <LicenseStatusCell name={def.name} expireDate={found?.expireDate}>
                    <LicenseStatusCounts item={item} />
                    <small className="text-muted d-block mt-1" data-testid="overview-status-only-caption">
                        {t('license.message.statusOnlyNoOrgDetail')}
                    </small>
                </LicenseStatusCell>
            </div>
        );
    };

    return (
        <div className="row g-2" role="group" aria-label={t('license.label.overview')}>
            {LICENSE_PRODUCTS.map((p) => (
                <div key={p.key} className={CARD_COL_CLASS}>
                    <LicenseProductStatusCard
                        product={p}
                        user={user}
                        role={role}
                        selected={p.key === selectedProductKey}
                        onSelect={onSelectProduct ? () => onSelectProduct(p.key) : undefined}
                    />
                </div>
            ))}
            {!isDealer && FLAT_LICENSE_IDS.map(renderFlatLicense)}
        </div>
    );
};

const mapState = (state) => ({
    licenses: state.LicenseReducer.licenses,
});

export default connect(mapState)(LicenseOverviewTab);
